/**
 * Email normalization and validation for backend ingestion paths (auto-enroll,
 * user provisioning). Normalize before storing or comparing so that case and
 * whitespace differences don't create duplicate records.
 */

/**
 * Pragmatic RFC-5322-subset check: exactly one `@`, no whitespace, and a dotted
 * domain with a 2+ char final label. Deliberately not exhaustive — it rejects
 * the obviously broken and leaves the mail provider as the final arbiter of
 * deliverability.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Characters that mean the value was already damaged before it got here.
 *
 * `U+FFFD` is the replacement character — what a decoder writes when it meets bytes it cannot
 * read. It is never part of an address; it is the record of one having been corrupted. Three
 * North Carolina Central enrollments are stored with it in front of the local part because a
 * CSV's UTF-8 byte-order mark was decoded wrongly upstream, and none of those three can ever
 * activate: the address Auth0 sends at sign-in will never match the one on the row.
 *
 * `trim()` does not remove it — it is not whitespace, unlike the `U+FEFF` it was mangled from —
 * and `[^\s@]` matches it, so every check this module had accepted the address as valid.
 *
 * C0 and C1 controls are here for the same reason: whatever produced one, an address containing
 * it cannot be delivered to, and storing it loses the person rather than the character.
 */
const CORRUPTED_RE = /[\uFFFD\u0000-\u001F\u007F-\u009F]/;

/**
 * Zero-width marks a decoder may leave intact at the front of a field.
 *
 * `U+FEFF` is whitespace to `trim()`, so this is belt and braces for it — but the zero-width
 * space and joiner are not, and a spreadsheet export is where they come from. Stripped rather
 * than rejected: unlike a replacement character, nothing has been lost yet.
 */
const INVISIBLE_RE = /[\uFEFF\u200B-\u200D]/g;

/** Lower-cases and trims an email for stable storage and comparison. */
export const normalizeEmail = (email: string): string => email.replace(INVISIBLE_RE, "").trim().toLowerCase();

/** Returns true when the (trimmed) value looks like a deliverable email address. */
export const isValidEmail = (email: string): boolean => {
    const trimmed = email.replace(INVISIBLE_RE, "").trim();
    return !CORRUPTED_RE.test(trimmed) && EMAIL_RE.test(trimmed);
};
